/**
 * RetailID QR URL generation utilities.
 * Uses @metrc/retailid SDK to generate QR URLs from package tags and index ranges.
 */

import { ObjectId, getShortUrl } from '@metrc/retailid';

/**
 * A range is a tuple of [start, end] (inclusive on both ends).
 * For example, [0, 99] represents indices 0 through 99.
 */
export type Range = [number, number];

/**
 * An array of ranges.
 */
export type Ranges = Range[];

/**
 * Options for QR URL generation.
 */
export interface GenerateQrUrlsOptions {
  /** 24-character hex string (package tag/label) */
  packageTag: string;
  /** Array of [start, end] ranges (inclusive) */
  ranges: Ranges;
  /** Custom domain (defaults to 1A4.COM) */
  domain?: string;
  /** Use legacy base64 encoding instead of base36 */
  base64?: boolean;
}

/**
 * Validates that a string is a valid 24-character hex string.
 */
export function isValidPackageTag(tag: string): boolean {
  if (!tag || typeof tag !== 'string') return false;
  if (tag.length !== 24) return false;
  return /^[0-9a-fA-F]{24}$/.test(tag);
}

/**
 * Expands ranges to a flat array of indices.
 * For example, [[0, 2], [5, 6]] becomes [0, 1, 2, 5, 6].
 */
export function rangesToFlat(ranges: Ranges): number[] {
  const indices: number[] = [];
  for (const range of ranges) {
    if (!Array.isArray(range) || range.length < 1) continue;
    
    // Handle single-element ranges [x] as [x, x]
    const start = range[0];
    const end = range.length > 1 ? range[1] : range[0];
    
    for (let i = start; i <= end; i++) {
      indices.push(i);
    }
  }
  return indices;
}

/**
 * Generates RetailID QR URLs for the given package tag and ranges.
 * 
 * @param options - Generation options
 * @returns Array of QR URLs in order of the expanded range indices
 * 
 * @example
 * const urls = generateRetailIdQrUrls({
 *   packageTag: '1A4060300000001000000123',
 *   ranges: [[0, 2]],
 * });
 * // Returns ['HTTPS://1A4.COM/...', 'HTTPS://1A4.COM/...', 'HTTPS://1A4.COM/...']
 */
export function generateRetailIdQrUrls(options: GenerateQrUrlsOptions): string[] {
  const { packageTag, ranges, domain, base64 } = options;

  if (!isValidPackageTag(packageTag)) {
    console.warn('[retailIdQr] Invalid package tag:', packageTag);
    return [];
  }

  if (!ranges || !Array.isArray(ranges) || ranges.length === 0) {
    console.warn('[retailIdQr] No ranges provided');
    return [];
  }

  try {
    const batchId = new ObjectId(packageTag);
    const indices = rangesToFlat(ranges);
    const urls: string[] = [];

    for (const index of indices) {
      const url = getShortUrl(batchId, index, { domain, base64 });
      urls.push(url);
    }

    return urls;
  } catch (error) {
    console.error('[retailIdQr] Error generating QR URLs:', error);
    return [];
  }
}

/**
 * Generates RetailID QR URLs for a simple sequential range.
 * Convenience function for when you have an offset and count instead of explicit ranges.
 * 
 * @param packageTag - 24-character hex string
 * @param offset - Starting index (default 0)
 * @param count - Number of QR codes to generate
 * @param options - Additional options
 * @returns Array of QR URLs
 * 
 * @example
 * const urls = generateRetailIdQrUrlsSequential('1A4060300000001000000123', 0, 100);
 * // Returns 100 QR URLs for indices 0-99
 */
export function generateRetailIdQrUrlsSequential(
  packageTag: string,
  offset: number,
  count: number,
  options?: { domain?: string; base64?: boolean }
): string[] {
  if (count <= 0) return [];
  
  const ranges: Ranges = [[offset, offset + count - 1]];
  return generateRetailIdQrUrls({
    packageTag,
    ranges,
    ...options,
  });
}

/**
 * Progressive QR URL generation with periodic progress callbacks.
 * Useful for long-running generation where UI should stay responsive.
 */
export async function generateRetailIdQrUrlsSequentialProgressive(
  packageTag: string,
  offset: number,
  count: number,
  options?: { domain?: string; base64?: boolean; chunkSize?: number },
  onProgress?: (generated: number, total: number) => void,
): Promise<string[]> {
  if (count <= 0) return [];
  if (!isValidPackageTag(packageTag)) {
    console.warn('[retailIdQr] Invalid package tag:', packageTag);
    return [];
  }

  const batchId = new ObjectId(packageTag);
  const urls: string[] = [];
  const total = count;
  const chunkSize = Math.max(25, options?.chunkSize ?? 200);

  for (let i = 0; i < total; i++) {
    const index = offset + i;
    const url = getShortUrl(batchId, index, { domain: options?.domain, base64: options?.base64 });
    urls.push(url);

    const generated = i + 1;
    if (onProgress && (generated === total || generated % chunkSize === 0)) {
      onProgress(generated, total);
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }

  return urls;
}

/**
 * Generates a single RetailID QR URL for a specific index.
 * 
 * @param packageTag - 24-character hex string
 * @param index - The label index
 * @param options - Additional options
 * @returns The QR URL, or empty string on error
 */
export function generateRetailIdQrUrl(
  packageTag: string,
  index: number,
  options?: { domain?: string; base64?: boolean }
): string {
  if (!isValidPackageTag(packageTag)) {
    console.warn('[retailIdQr] Invalid package tag:', packageTag);
    return '';
  }

  try {
    const batchId = new ObjectId(packageTag);
    return getShortUrl(batchId, index, options);
  } catch (error) {
    console.error('[retailIdQr] Error generating QR URL:', error);
    return '';
  }
}
